const GPT_MODEL = {
    O4_MINI: 'o4-mini',
    O3_MINI: 'o3-mini',
    GPT_4O: 'gpt-4o',
    GPT_4_1: 'gpt-4.1',
    GPT_5: 'gpt-5',
    GPT_5_2: 'gpt-5.2',
    GPT_5_4: 'gpt-5.4',
    GPT_5_4_MINI: 'gpt-5.4-mini',
    GPT_5_5: 'gpt-5.5',
}

const GPT_MODEL_SETTING = {
    GPT_4O: {
        KEY: 'gpt-4o',
        VALUE: 'gpt-4o',
        TAG: 'field_chatgpt_model_ai_auto_tag_4o'
    },
    O3_MINI: {
        KEY: 'o3-mini',
        VALUE: 'o3-mini',
        TAG: 'field_chatgpt_model_ai_auto_tag_o3'
    },
    O4_MINI: {
        KEY: 'o4-mini',
        VALUE: 'o4-mini',
        TAG: 'field_chatgpt_model_ai_auto_tag_o4' 
    },
    GPT_4_1:{
        KEY: 'gpt-4.1',
        VALUE: 'gpt-4.1',
        TAG: 'field_chatgpt_model_ai_auto_tag_41'
    },
    GPT_5:{
        KEY: 'gpt-5',
        VALUE: 'gpt-5',
        TAG: 'field_chatgpt_model_ai_auto_tag_5'
    },
    GPT_5_2:{
        KEY: 'gpt-5.2',
        VALUE: 'gpt-5.2',
        TAG: 'field_chatgpt_model_ai_auto_tag_52'
    },
    GPT_5_4:{
        KEY: 'gpt-5.4',
        VALUE: 'gpt-5.4',
        TAG: 'field_chatgpt_model_ai_auto_tag_54'
    },
    GPT_5_4_MINI:{
        KEY: 'gpt-5.4-mini',
        VALUE: 'gpt-5.4-mini',
        TAG: 'field_chatgpt_model_ai_auto_tag_54_mini'
    },
    GPT_5_5:{
        KEY: 'gpt-5.5',
        VALUE: 'gpt-5.5',
        TAG: 'field_chatgpt_model_ai_auto_tag_55'
    }
}

const REASONING_EFFORT_OPTIONS = {
        NONE: {
            KEY: 'none',
            VALUE: 'none',
            TAG: 'field_reasoning_effort_ai_auto_none'
        },
        MINIMAL: {
            KEY: 'minimal',
            VALUE: 'minimal',
            TAG: 'field_reasoning_effort_ai_auto_minimal'
        },
        LOW: {
            KEY: 'low',
            VALUE: 'low',
            TAG: 'field_reasoning_effort_ai_auto_low'
        },
        MEDIUM: {
            KEY: 'medium',
            VALUE: 'medium',
            TAG: 'field_reasoning_effort_ai_auto_medium' 
        },
        HIGH:{
            KEY: 'high',
            VALUE: 'high',
            TAG: 'field_reasoning_effort_ai_auto_high'
        },
        XHIGH: {
            KEY: 'xhigh',
            VALUE: 'xhigh',
            TAG: 'field_reasoning_effort_ai_auto_xhigh'
        },
};

/**
 * Check if model supports multimodal input (image upload/analysis).
 * gpt-4o, gpt-4.1, gpt-5.2, gpt-5.4, gpt-5.4-mini: always supported.
 * gpt-5: supported only when reasoning effort is not MINIMAL.
 * Note: This is different from image generation capability.
 */
const isSupportMultiModal = (chatGptModel, usingReasoningEffort) => {
    if (
        [
            GPT_MODEL.GPT_4O,
            GPT_MODEL.GPT_4_1,
            GPT_MODEL.GPT_5_2,
            GPT_MODEL.GPT_5_4,
            GPT_MODEL.GPT_5_4_MINI,
            GPT_MODEL.GPT_5_5
        ].includes(chatGptModel)
    )
        return true;
    
    if (chatGptModel === GPT_MODEL.GPT_5) {
        if (!usingReasoningEffort) return false;
        
        const isSelectMinimal = usingReasoningEffort === REASONING_EFFORT_OPTIONS.MINIMAL.VALUE;
        return !isSelectMinimal;
    }
    
    return false;
};

/**
 * Check if model supports image generation tool.
 * gpt-5.4-mini: supports multimodal input (image analysis) but NOT image generation.
 */
const isSupportImageGeneration = (chatGptModel, usingReasoningEffort) => {
    // gpt-5.4-mini does NOT support image generation
    if (chatGptModel === GPT_MODEL.GPT_5_4_MINI) {
        return false;
    }
    // Other models: use same logic as multimodal support
    return isSupportMultiModal(chatGptModel, usingReasoningEffort);
};

/**
 * Get list of ChatGPT models from corporation detail setting.
 */
const getListChatGptModelFromCorpDetail = (corporationDetail, isOriginList = false) => {
    try {
        const setting = JSON.parse(corporationDetail?.corporationSetting || "{}");
        const models = Array.isArray(setting.listChatGptModel) ? setting.listChatGptModel : [];
        if (isOriginList) {
            return models;
        }
        return models.filter(m => m?.id).map(m => ({ id: m.id, name: m.id }))
    } catch {
        return [];
    }
};

/**
 * Get allowed reasoning effort option values for a given model.
 * gpt-5: MINIMAL, LOW, MEDIUM, HIGH (no NONE/XHIGH).
 * gpt-5.2/gpt-5.4: NONE, LOW, MEDIUM, HIGH, XHIGH (no MINIMAL).
 * Other/null: all options.
 */
 const getReasoningEffortOptionsForModel = modelId => {
    const reasoningEffortOptionValues = Object.values(REASONING_EFFORT_OPTIONS).map(
        i => i['VALUE']
    );
    if (!modelId) return reasoningEffortOptionValues;
    
    // NONE and XHIGH are only for GPT-5.2
    const gpt52OnlyOptions = [
        REASONING_EFFORT_OPTIONS.NONE.VALUE,
        REASONING_EFFORT_OPTIONS.XHIGH.VALUE
    ];
    
    if (modelId === GPT_MODEL.GPT_5) {
        return reasoningEffortOptionValues.filter(
            i => !gpt52OnlyOptions.includes(i)
        );
    }
        
    if ([GPT_MODEL.GPT_5_2, GPT_MODEL.GPT_5_4, GPT_MODEL.GPT_5_4_MINI, GPT_MODEL.GPT_5_5].includes(modelId)) {
        return reasoningEffortOptionValues.filter(
            i => i !== REASONING_EFFORT_OPTIONS.MINIMAL.VALUE
        );
    }
        
    return [];
};

/**
 * Resolve model name value from system tag. Returns null if not found.

 */
const getModelIdFromTagSysField = (sysField) => {
    if (!sysField) return null;

    const options = GPT_MODEL_SETTING;

    for (const key in options) {
        if (options[key].TAG === sysField) {
            return options[key].VALUE;
        }
    }

    return null;
};


/**
 * Validate reasoning effort option compatibility with gpt-5 family models.
 * gpt-5: NONE and XHIGH are not allowed.
 * gpt-5.2/gpt-5.4: MINIMAL is not allowed.
 */
const validateforGpt5 = (sysField, itemDataSystemField) => {
    // Check if model is gpt-5
    const isGpt5 = sysField === GPT_MODEL_SETTING.GPT_5.TAG;
    // Check if model is gpt-5.2 or gpt-5.4 (extended models)
    const isGpt5Extended = [GPT_MODEL_SETTING.GPT_5_2.TAG, GPT_MODEL_SETTING.GPT_5_4.TAG, GPT_MODEL_SETTING.GPT_5_4_MINI.TAG, GPT_MODEL_SETTING.GPT_5_5.TAG].includes(sysField);
    // NONE and XHIGH are only for GPT-5.2+
    const gpt52OnlyOptions = [REASONING_EFFORT_OPTIONS.NONE.TAG, REASONING_EFFORT_OPTIONS.XHIGH.TAG];
    // For gpt-5: exclude NONE and XHIGH options
    if (isGpt5) {
        return !gpt52OnlyOptions.includes(itemDataSystemField);
    }
    // For gpt-5.2/gpt-5.4: exclude MINIMAL option (NONE and XHIGH are allowed)
    if (isGpt5Extended) {
        return itemDataSystemField !== REASONING_EFFORT_OPTIONS.MINIMAL.TAG;
    }
    return true;
}

/**
 * Check if model belongs to gpt-5 family (gpt-5, gpt-5.2, gpt-5.4) by model name.
 */
const isGpt5Model = (chatGptModel) => {
    return [
        GPT_MODEL_SETTING.GPT_5.VALUE, 
        GPT_MODEL_SETTING.GPT_5_2.VALUE,
        GPT_MODEL_SETTING.GPT_5_4.VALUE,
        GPT_MODEL_SETTING.GPT_5_4_MINI.VALUE,
        GPT_MODEL_SETTING.GPT_5_5.VALUE
    ].includes(chatGptModel)
}

/**
 * Check if model belongs to gpt-5 family by system tag.
 */
const isSupportReasoningBySystemTag = (sysField) => {
    return [
        GPT_MODEL_SETTING.GPT_5.TAG, 
        GPT_MODEL_SETTING.GPT_5_2.TAG, 
        GPT_MODEL_SETTING.GPT_5_4.TAG, 
        GPT_MODEL_SETTING.GPT_5_4_MINI.TAG,
        GPT_MODEL_SETTING.GPT_5_5.TAG
    ].includes(sysField)
}

/**
 * Get default reasoning effort value by model.
 * gpt-5: MINIMAL
 * gpt-5.2/gpt-5.4: NONE
 * other models: MEDIUM (fallback)
 */
const defaultReasoningEffort = (chatGptModel, isGetTagValue = false) => {
    if (chatGptModel === GPT_MODEL_SETTING.GPT_5.VALUE) {
        if (isGetTagValue) return REASONING_EFFORT_OPTIONS.MINIMAL.TAG;
        return REASONING_EFFORT_OPTIONS.MINIMAL.VALUE;
    }
    if (isGetTagValue) return REASONING_EFFORT_OPTIONS.NONE.TAG;

    return REASONING_EFFORT_OPTIONS.NONE.VALUE;
}

/**
 * Get all reasoning effort system tags.
 */
const getReasoningEffortTags = () => {
    return Object.values(REASONING_EFFORT_OPTIONS).map(v => v.TAG)
}


/**
 * Check if model supports reasoning effort parameter. Only gpt-5 family models support it.
 */
const isSupportReasoning = (model) => {
     return [
        GPT_MODEL_SETTING.GPT_5.VALUE, 
        GPT_MODEL_SETTING.GPT_5_2.VALUE,
        GPT_MODEL_SETTING.GPT_5_4.VALUE,
        GPT_MODEL_SETTING.GPT_5_4_MINI.VALUE,
        GPT_MODEL_SETTING.GPT_5_5.VALUE,
    ].includes(model)
}


const OpenAiUtil = {
    isSupportMultiModal,
    isSupportImageGeneration,
    getListChatGptModelFromCorpDetail,
    getReasoningEffortOptionsForModel,
    isGpt5Model,
    isSupportReasoningBySystemTag,
    defaultReasoningEffort,
    getReasoningEffortTags,
    isSupportReasoning,
    getModelIdFromTagSysField,
    validateforGpt5
};

export default OpenAiUtil;

